import React, { useState } from 'react';
import { Calendar, Users, BarChart3, MessageSquare, Trophy, Clock, Target, ChevronRight, Bell, Settings, LogOut, Star, TrendingUp, Activity } from 'lucide-react';
import './App.css';

const SportsCoachingApp = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Sample data
  const players = [
    { id: 1, name: 'Sarah Johnson', sport: 'Tennis', level: 'Intermediate', parent: 'Mike Johnson', lastActive: '2 hours ago', rank: 15, points: 1240 },
    { id: 2, name: 'Alex Chen', sport: 'Volleyball', level: 'Advanced', parent: 'Lisa Chen', lastActive: '1 day ago', rank: 8, points: 1580 },
    { id: 3, name: 'Emma Davis', sport: 'Pickleball', level: 'Beginner', parent: 'Tom Davis', lastActive: '3 hours ago', rank: 22, points: 890 },
    { id: 4, name: 'Jake Wilson', sport: 'Tennis', level: 'Advanced', parent: 'Rachel Wilson', lastActive: '5 hours ago', rank: 3, points: 1890 },
    { id: 5, name: 'Maya Patel', sport: 'Volleyball', level: 'Intermediate', parent: 'Raj Patel', lastActive: '1 hour ago', rank: 12, points: 1320 }
  ];

  const schedules = [
    { id: 1, date: 'Aug 24, 2025', time: '4:00 PM', activity: 'Tennis Training', location: 'Court 1', players: ['Sarah Johnson', 'Jake Wilson'] },
    { id: 2, date: 'Aug 25, 2025', time: '10:00 AM', activity: 'Volleyball Practice', location: 'Gym A', players: ['Alex Chen', 'Maya Patel'] },
    { id: 3, date: 'Aug 26, 2025', time: '3:00 PM', activity: 'Pickleball Basics', location: 'Court 3', players: ['Emma Davis'] },
    { id: 4, date: 'Aug 27, 2025', time: '5:30 PM', activity: 'Tennis Tournament Prep', location: 'Court 1-2', players: ['Sarah Johnson', 'Jake Wilson'] }
  ];

  const announcements = [
    { id: 1, title: 'Tournament Results Posted', content: 'Great job everyone at the regional tournament!', date: 'Aug 20, 2025', priority: 'high' },
    { id: 2, title: 'New Training Equipment', content: 'We have new ball machines available for practice sessions.', date: 'Aug 18, 2025', priority: 'medium' },
    { id: 3, title: 'Schedule Update', content: 'Thursday practice moved to 4:30 PM due to court maintenance.', date: 'Aug 15, 2025', priority: 'high' }
  ];

  // Login Component
  const LoginScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="text-white w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">SportsPro</h1>
          <p className="text-gray-600 mt-2">Elite Training Management</p>
        </div>
        
        <div className="space-y-4">
          <button 
            onClick={() => setCurrentUser({ type: 'player', name: 'Sarah Johnson', sport: 'Tennis' })}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Demo as Player/Parent
          </button>
          <button 
            onClick={() => setCurrentUser({ type: 'coach', name: 'Coach Martinez' })}
            className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Demo as Coach
          </button>
        </div>
        
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Demo Version - Explore both portals</p>
        </div>
      </div>
    </div>
  );

  // Player/Parent Dashboard
  const PlayerDashboard = () => (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Welcome back, Sarah!</h2>
            <p className="opacity-90 mt-1">Your next training is in 2 days</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">Rank #15</div>
            <div className="text-sm opacity-90">1,240 points</div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Training Hours</p>
              <p className="text-2xl font-bold text-gray-800">24.5</p>
              <p className="text-green-500 text-sm flex items-center mt-1">
                <TrendingUp className="w-4 h-4 mr-1" />
                +12% this month
              </p>
            </div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Win Rate</p>
              <p className="text-2xl font-bold text-gray-800">78%</p>
              <p className="text-green-500 text-sm flex items-center mt-1">
                <TrendingUp className="w-4 h-4 mr-1" />
                +5% this week
              </p>
            </div>
            <Target className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Skill Level</p>
              <p className="text-2xl font-bold text-gray-800">8.2</p>
              <p className="text-blue-500 text-sm flex items-center mt-1">
                <Star className="w-4 h-4 mr-1" />
                Intermediate+
              </p>
            </div>
            <Activity className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Upcoming Sessions */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Upcoming Sessions</h3>
        </div>
        <div className="p-6 space-y-4">
          {schedules.slice(0, 3).map(session => (
            <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center space-x-4">
                <div className="bg-blue-500 text-white w-12 h-12 rounded-lg flex items-center justify-center font-semibold">
                  {session.date.split(' ')[1].split(',')[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{session.activity}</p>
                  <p className="text-gray-600 text-sm">{session.time} • {session.location}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          ))}
        </div>
      </div>

      {/* Recent Announcements */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Coach Updates</h3>
        </div>
        <div className="p-6 space-y-4">
          {announcements.slice(0, 2).map(announcement => (
            <div key={announcement.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <Bell className={`w-5 h-5 mt-1 ${announcement.priority === 'high' ? 'text-red-500' : 'text-blue-500'}`} />
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{announcement.title}</p>
                <p className="text-gray-600 text-sm mt-1">{announcement.content}</p>
                <p className="text-gray-400 text-xs mt-2">{announcement.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Coach Dashboard
  const CoachDashboard = () => (
    <div className="space-y-6">
      {/* Coach Overview */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Coach Dashboard</h2>
            <p className="opacity-90 mt-1">Managing 24 athletes across 3 sports</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">5</div>
            <div className="text-sm opacity-90">sessions today</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-left">
          <Users className="w-8 h-8 text-blue-500 mb-3" />
          <p className="font-semibold text-gray-800">Manage Players</p>
          <p className="text-gray-600 text-sm">24 active athletes</p>
        </button>
        <button className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-left">
          <Calendar className="w-8 h-8 text-green-500 mb-3" />
          <p className="font-semibold text-gray-800">Schedule</p>
          <p className="text-gray-600 text-sm">12 upcoming sessions</p>
        </button>
        <button className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-left">
          <BarChart3 className="w-8 h-8 text-purple-500 mb-3" />
          <p className="font-semibold text-gray-800">Analytics</p>
          <p className="text-gray-600 text-sm">Performance data</p>
        </button>
        <button className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-left">
          <MessageSquare className="w-8 h-8 text-orange-500 mb-3" />
          <p className="font-semibold text-gray-800">Communications</p>
          <p className="text-gray-600 text-sm">Send updates</p>
        </button>
      </div>

      {/* Recent Players Activity */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Player Overview</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {players.slice(0, 4).map(player => (
              <div key={player.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm">
                    {player.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{player.name}</p>
                    <p className="text-gray-600 text-sm">{player.sport} • {player.level}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-800">Rank #{player.rank}</p>
                  <p className="text-gray-600 text-xs">{player.lastActive}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's Schedule */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Today's Schedule</h3>
        </div>
        <div className="p-6 space-y-4">
          {schedules.slice(0, 3).map(session => (
            <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="bg-green-500 text-white w-12 h-12 rounded-lg flex items-center justify-center font-semibold text-sm">
                  {session.time.split(':')[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{session.activity}</p>
                  <p className="text-gray-600 text-sm">{session.location} • {session.players.length} players</p>
                </div>
              </div>
              <button className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors">
                Manage
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Main App Shell
  const AppShell = () => {
    const isCoach = currentUser?.type === 'coach';
    
    const navigation = isCoach ? [
      { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
      { id: 'players', label: 'Players', icon: Users },
      { id: 'schedule', label: 'Schedule', icon: Calendar },
      { id: 'communications', label: 'Communications', icon: MessageSquare }
    ] : [
      { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
      { id: 'schedule', label: 'My Schedule', icon: Calendar },
      { id: 'stats', label: 'Stats', icon: Trophy },
      { id: 'messages', label: 'Messages', icon: MessageSquare }
    ];

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-10 h-10 rounded-lg flex items-center justify-center">
                  <Trophy className="text-white w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">SportsPro</h1>
                  <p className="text-xs text-gray-600">{isCoach ? 'Coach Portal' : 'Player Portal'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-800">{currentUser.name}</p>
                  <p className="text-xs text-gray-600">{isCoach ? 'Coach' : currentUser.sport}</p>
                </div>
                <button 
                  onClick={() => setCurrentUser(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex space-x-8">
            {/* Sidebar Navigation */}
            <div className="w-64 space-y-2">
              {navigation.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    activeTab === item.id 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {activeTab === 'dashboard' && (isCoach ? <CoachDashboard /> : <PlayerDashboard />)}
              {activeTab !== 'dashboard' && (
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
                  <div className="text-6xl mb-4">🚧</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {navigation.find(n => n.id === activeTab)?.label} - Coming Soon
                  </h3>
                  <p className="text-gray-600">
                    This section will include detailed {activeTab} management features in the full app.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!currentUser) {
    return <LoginScreen />;
  }

  return <AppShell />;
};

export default SportsCoachingApp;